/**
 * The register's own life: the authoritative record everything else on the page derives from.
 */

import { useJurisdiction, useJurisdictions, useMeta } from '~/api/queries';
import { useParams } from 'react-router';
import { useScreen } from '~/layout/RouteAnnouncer';
import { QueryError } from '~/layout/ErrorBoundary';
import { MetricGrid, MetricTile, SectionCard, SkeletonStack } from '~/ui/Surface';
import { EmptyState } from '~/ui/Feedback';
import { LifecycleBandChart } from './LifecycleBandChart';
import { LifecycleEditor, matterLabelMap } from './LifecycleEditor';
import { SuccessionPanel } from './SuccessionPanel';
import { TemporalBanner } from '../matter/TemporalBanner';
import { fmtDate, fmtYear } from '~/lib/format';

export function OverviewTab() {
  const { code = '' } = useParams();
  const register = useJurisdiction(code);
  const meta = useMeta();
  // The codes already held, so the succession form can say when adding a name creates a register.
  const known = useJurisdictions({ pageSize: 200 });

  useScreen(register.data ? `${register.data.name} — Overview` : null);

  if (register.isError) {
    return <QueryError error={register.error} onRetry={() => void register.refetch()} />;
  }
  if (register.isPending) return <SkeletonStack rows={5} height={56} />;

  const data = register.data;
  const renames = data.lifecycle.filter((event) => event.kind === 'renamed').length;
  const versions = data.matters.reduce((sum, matter) => sum + matter.versions, 0);
  const axisEnd = data.closedOn ?? data.closingOn ?? data.today;

  return (
    <>
      <TemporalBanner
        asAt={data.asAt}
        recordedAt={data.recordedAt}
        historical={data.historical}
        today={data.today}
      />

      <MetricGrid>
        <MetricTile value={data.founded ?? '—'} label="Year opened" />
        <MetricTile value={renames} label={renames === 1 ? 'Rename' : 'Renames'} />
        <MetricTile
          value={data.closedOn ? fmtYear(data.closedOn) : 'Still open'}
          label={data.closingOn ? 'Closes' : 'Closed'}
        />
        <MetricTile value={versions} label="Rule texts" />
      </MetricGrid>

      <SectionCard
        title="What this register has been called"
        note="A right is searched for under the name the register carried on its filing date, so the bands are what a searcher actually needs — not the events. A band drawn as a dashed outline begins after today."
      >
        {data.nameBands.length === 0 ? (
          <EmptyState variant="bare">
            <p>
              This register has no recorded opening, so there is no span to draw names over. Add
              an opening date below.
            </p>
          </EmptyState>
        ) : (
          <LifecycleBandChart
            bands={data.nameBands}
            events={data.lifecycle}
            registerName={data.name}
            axisEnd={axisEnd}
          />
        )}
      </SectionCard>

      <SectionCard
        title="Lifecycle events"
        note={`Status, current name and the span both rule chains are laid out over are all derived from this list, so correcting one date here redraws every matter tab. A change dated after ${fmtDate(data.today)} is recorded now and reads as scheduled until it takes effect.`}
      >
        <LifecycleEditor register={data} matterLabels={matterLabelMap(data.matters)} />
      </SectionCard>

      <SectionCard
        title="Succession"
        note="Where rights went when this register closed, and where its own rights came from. A closed register keeps its rules; succession says who else a right may now fall due at."
      >
        <SuccessionPanel
          code={data.code}
          successors={data.successors}
          predecessors={data.predecessors}
          matters={meta.data?.matters ?? []}
          knownCodes={known.data?.jurisdictions.map((row) => row.code) ?? []}
          today={data.today}
        />
      </SectionCard>
    </>
  );
}
